import { deleteCookie } from "cookies-next";
import type { User } from "firebase/auth";
import { AUTH_COOKIE_NAME } from "../const";
import getCookieMaxAgeSeconds from "../utils/getCookieMaxAgeSeconds";
import preauthSession from "./preauthSession";

export type EstablishedSession = {
  token: string;
  expirationTime: string;
  reauthenticated: boolean;
};

const DEDUPE_WINDOW_MS = 5_000;

let inFlight: Promise<EstablishedSession> | null = null;
let inFlightUid: string | null = null;
let lastSession: {
  uid: string;
  session: EstablishedSession;
  at: number;
} | null = null;

export function clearSessionEstablishment() {
  inFlight = null;
  inFlightUid = null;
  lastSession = null;
}

/**
 * Shared preauth + httpOnly cookie write so sign-in and onIdTokenChanged
 * do not double-hit the account service for the same user.
 */
export default async function establishSession(
  user: User,
  serviceLocale: string | undefined,
): Promise<EstablishedSession> {
  if (!serviceLocale) {
    throw new Error("Missing service locale");
  }

  if (
    lastSession?.uid === user.uid &&
    Date.now() - lastSession.at < DEDUPE_WINDOW_MS
  ) {
    return lastSession.session;
  }

  if (inFlight && inFlightUid === user.uid) {
    return inFlight;
  }

  inFlightUid = user.uid;
  inFlight = (async () => {
    const session = await preauthSession(user, serviceLocale);

    if (!session.ok) {
      throw session.error instanceof Error
        ? session.error
        : new Error("Failed to validate session");
    }

    const maxAge = getCookieMaxAgeSeconds(session.expirationTime);

    // Remove any legacy client-writable cookie before the httpOnly write.
    deleteCookie(AUTH_COOKIE_NAME);

    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: session.token,
        expirationTime: session.expirationTime,
        maxAge,
        locale: serviceLocale,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to establish auth session cookie");
    }

    const established: EstablishedSession = {
      token: session.token,
      expirationTime: session.expirationTime,
      reauthenticated: session.reauthenticated,
    };

    lastSession = { uid: user.uid, session: established, at: Date.now() };
    return established;
  })().finally(() => {
    inFlight = null;
    inFlightUid = null;
  });

  return inFlight;
}
